import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';

import { BusinessEntityProvider } from '@zenbusiness/application-commons-business-entity';
import { ClickstreamScene } from '@zenbusiness/application-commons-clickstream';
import { EnvProvider } from '@zenbusiness/application-commons-env';
import PageError from '@zenbusiness/zui/PageError';

import DevelopmentGate from '#client/App/DevelopmentGate';
import DevToolProviders from '#client/App/DevToolProviders';
import ExperimentsWrapper from '#client/App/ExperimentsWrapper';
import FlowPageGate from '#client/App/Flow/FlowPageGate';
import { FlowConfigProvider } from '#client/App/FlowConfigProvider';
import FlowProviders from '#client/App/FlowProviders';
import { NotificationProvider } from '#client/App/NotificationManager';
import PostRouting from '#client/App/PostRouting';
import { DEFAULT_SEGMENT, NAME } from '#client/build-env';
import QueryParams from '#client/components/QueryParams';
import ShoppingErrorBoundary from '#client/components/ShoppingErrorBoundary';
import Template from '#client/components/Template';
import TrackProductView from '#client/components/TrackProductView';
import FLOW_CONFIG from '#client/config/flowConfig';
import * as ROUTES from '#client/constants/routes';
import * as Pages from '#client/pages';

const BROWSER_ROUTER_OPTIONS = {
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
    v7_startTransition: true
  }
};

const BubbleError = () => {
  const error = useRouteError();
  if (error) {
    // Rethrow the error to propagate to a higher boundary.
    throw error;
  }
  // Prevent React Router from rendering its error handler.
  return null;
};

const BASE_ROUTES = [
  {
    /**
     * Routes which do not require authentication at page load.
     */
    element: <PostRouting />,
    children: [
      { path: ROUTES.AUTH_LOGIN, element: <Pages.Login /> },
      { path: ROUTES.AUTH_LOGOUT, element: <Pages.Logout /> },
      { path: ROUTES.AUTH_CALLBACK, element: <Pages.Callback /> }
    ]
  },
  {
    /**
     * Conditionally available route for local and dev environments
     */
    path: ROUTES.SHOP_DASHBOARD,
    element: (
      <DevelopmentGate>
        <ExperimentsWrapper flowConfig={{ id: 'dashboard' }}>
          {() => (
            <NotificationProvider>
              <DevToolProviders>
                <Template>
                  <PostRouting />
                </Template>
              </DevToolProviders>
            </NotificationProvider>
          )}
        </ExperimentsWrapper>
      </DevelopmentGate>
    ),
    children: [{ index: true, element: <Pages.Dashboard /> }]
  },
  {
    /**
     * The root path for the application
     */
    path: ROUTES.SHOP,
    element: (
      <Template>
        <Pages.Shop />
      </Template>
    )
  },
  {
    path: '*',
    element: (
      <Template>
        <PageError>The requested route could not be found.</PageError>
      </Template>
    )
  }
];

function FlowRoute({ flowConfig }) {
  const { id: flowId } = flowConfig;
  const ErrorBoundaryFallback = flowConfig.errorBoundaryFallback || PageError;

  return {
    path: `${DEFAULT_SEGMENT}/${flowId}`,
    errorElement: (
      <Template>
        <ShoppingErrorBoundary
          fallback={<ErrorBoundaryFallback />}
          id={`${NAME}/App/DirectRoutes/${flowId}`}
        >
          <BubbleError />
        </ShoppingErrorBoundary>
      </Template>
    ),
    element: (
      <ClickstreamScene appSection={`flow-${flowId}`}>
        <ExperimentsWrapper flowConfig={flowConfig}>
          {(activeConfig) => (
            <FlowConfigProvider flowConfig={activeConfig}>
              <BusinessEntityProvider autoLoad={activeConfig.autoLoadBusiness}>
                <FlowProviders>
                  <Template />
                </FlowProviders>
              </BusinessEntityProvider>
            </FlowConfigProvider>
          )}
        </ExperimentsWrapper>
      </ClickstreamScene>
    ),
    children: [
      {
        index: true,
        element: (
          <PostRouting>
            <QueryParams />
            <Pages.FlowEntry />
          </PostRouting>
        )
      },
      {
        path: ':flowPage',
        element: (
          <PostRouting>
            <FlowPageGate>
              <TrackProductView />
              <Pages.FlowPage />
            </FlowPageGate>
          </PostRouting>
        )
      }
    ]
  };
}

const FLOW_ROUTES = FLOW_CONFIG.map((flowConfig) => FlowRoute({ flowConfig }));
const ALL_ROUTES = BASE_ROUTES.concat(FLOW_ROUTES);
const router = createBrowserRouter(ALL_ROUTES, BROWSER_ROUTER_OPTIONS);

export function DirectRoutes() {
  return (
    // ROOT_SEGMENT is used to identify where the router is mounted at.
    // Since this is a direct entry router, the root is the MFE's DEFAULT_SEGMENT
    <EnvProvider env={{ ROOT_SEGMENT: DEFAULT_SEGMENT }}>
      <RouterProvider future={{ v7_startTransition: true }} router={router} />
    </EnvProvider>
  );
}
